/**
 * useAxelarWallet Hook
 *
 * React Query hooks for fetching Axelar account data.
 * Axelar is a cross-chain communication protocol using Cosmos SDK.
 */

import { useQuery, useQueries } from '@tanstack/react-query';
import { axelarApi, formatAXL, AXL_DENOM } from '../services/axelarApi';
import type {
  AxelarAccount,
  AxelarValidator,
  AxelarNetworkStats,
  AxelarSupportedChain,
  AxelarWalletData,
} from '../types/axelar.types';
import { useAxelarMonitorWallets } from './useMonitorWallets';

// Query keys
const QUERY_KEYS = {
  account: (address: string) => ['axelar', 'account', address],
  validators: ['axelar', 'validators'],
  networkStats: ['axelar', 'networkStats'],
  supportedChains: ['axelar', 'supportedChains'],
  price: ['axelar', 'price'],
};

/**
 * Fetch AXL price from CoinGecko
 */
async function fetchAxlPrice(): Promise<number> {
  try {
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=axelar&vs_currencies=usd'
    );
    if (!response.ok) throw new Error('Failed to fetch price');
    const data = await response.json();
    return data.axelar?.usd || 0;
  } catch (error) {
    console.error('[useAxelarWallet] Error fetching AXL price:', error);
    return 0;
  }
}

/**
 * Hook to get AXL price
 */
export function useAxlPrice() {
  return useQuery({
    queryKey: QUERY_KEYS.price,
    queryFn: fetchAxlPrice,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Hook to get account data for a single address
 */
export function useAxelarAccount(address: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.account(address || ''),
    queryFn: () => axelarApi.getAccount(address!),
    enabled: !!address && axelarApi.isValidAddress(address),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Hook to get all validators
 */
export function useAxelarValidators() {
  return useQuery({
    queryKey: QUERY_KEYS.validators,
    queryFn: () => axelarApi.getValidators(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to get network stats
 */
export function useAxelarNetworkStats() {
  return useQuery({
    queryKey: QUERY_KEYS.networkStats,
    queryFn: () => axelarApi.getNetworkStats(),
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get supported chains
 */
export function useAxelarSupportedChains() {
  return useQuery({
    queryKey: QUERY_KEYS.supportedChains,
    queryFn: () => axelarApi.getSupportedChains(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to get data for multiple Axelar wallets
 */
export function useAxelarWallets(addresses: string[]): AxelarWalletData[] {
  const validAddresses = addresses.filter((addr) => axelarApi.isValidAddress(addr));

  const queries = useQueries({
    queries: validAddresses.map((address) => ({
      queryKey: QUERY_KEYS.account(address),
      queryFn: () => axelarApi.getAccount(address),
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchInterval: 60 * 1000,
    })),
  });

  return validAddresses.map((address, index) => ({
    address,
    account: queries[index]?.data,
    isLoading: queries[index]?.isLoading || false,
    error: queries[index]?.error?.message,
  }));
}

/**
 * Main hook combining monitor wallets with Axelar data
 */
export function useAxelarMonitor() {
  const { wallets, isLoading: walletsLoading, ...walletActions } = useAxelarMonitorWallets();

  // Get addresses from monitor wallets
  const addresses = wallets.map((w) => w.address);

  // Fetch account data for all addresses
  const accountsData = useAxelarWallets(addresses);

  // Fetch price and network stats
  const { data: axlPrice = 0 } = useAxlPrice();
  const { data: networkStats } = useAxelarNetworkStats();
  const { data: validators = [] } = useAxelarValidators();

  // Combine wallet info with account data
  const walletsWithData: AxelarWalletData[] = wallets.map((wallet) => {
    const accountData = accountsData.find((a) => a.address === wallet.address);
    return {
      address: wallet.address,
      label: wallet.label,
      account: accountData?.account,
      isLoading: accountData?.isLoading || false,
      error: accountData?.error,
    };
  });

  // Calculate totals
  const totals = walletsWithData.reduce(
    (acc, wallet) => {
      if (wallet.account) {
        const availableBalance = wallet.account.balances.find((b) => b.denom === AXL_DENOM);
        acc.availableAXL += parseInt(availableBalance?.amount || '0', 10);
        acc.stakedAXL += parseInt(wallet.account.totalStaked, 10);
        acc.rewardsAXL += parseInt(wallet.account.totalRewards, 10);
        acc.unbondingAXL += parseInt(wallet.account.totalUnbonding, 10);
      }
      return acc;
    },
    { availableAXL: 0, stakedAXL: 0, rewardsAXL: 0, unbondingAXL: 0 }
  );

  const totalAXL =
    totals.availableAXL + totals.stakedAXL + totals.rewardsAXL + totals.unbondingAXL;

  return {
    wallets: walletsWithData,
    isLoading: walletsLoading || accountsData.some((a) => a.isLoading),
    axlPrice,
    networkStats,
    validators,
    totals: {
      availableAXL: formatAXL(totals.availableAXL.toString()),
      stakedAXL: formatAXL(totals.stakedAXL.toString()),
      rewardsAXL: formatAXL(totals.rewardsAXL.toString()),
      unbondingAXL: formatAXL(totals.unbondingAXL.toString()),
      totalAXL: formatAXL(totalAXL.toString()),
      totalUSD: (totalAXL / 1e6) * axlPrice,
    },
    ...walletActions,
  };
}
